import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';
import * as XLSX from 'xlsx';

const G = '#00C060';
const G_DARK = '#006633';
const BG = '#f0f7f2';
const CARD = '#ffffff';
const BORDER = 'rgba(0, 192, 96, 0.4)';
const SHADOW = '0 2px 12px rgba(0,0,0,0.08)';
const inputStyle = { background: '#f8faf8', borderColor: 'rgba(0,166,81,0.35)', color: '#1a3322' };

const Stocks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stocksData, setStocksData] = useState(null);
  const [pdvs, setPdvs] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [filters, setFilters] = useState({ city_id: '', pdv_id: '' });
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockPrize, setRestockPrize] = useState(null);
  const [restockQty, setRestockQty] = useState(0);
  const [restockDate, setRestockDate] = useState(new Date().toISOString().split('T')[0]);
  const [showGainModeModal, setShowGainModeModal] = useState(false);
  const [selectedSessionForGainMode, setSelectedSessionForGainMode] = useState(null);
  const [newGainMode, setNewGainMode] = useState('');
  const [addedStockMap] = useState({});

  const loadStocks = useCallback(async () => {
    try {
      const params = { brand: BRAND };
      if (filters.city_id) params.city_id = filters.city_id;
      if (filters.pdv_id) params.pdv_id = filters.pdv_id;
      const response = await api.get('/admin/stocks', { params });
      setStocksData(response.data.sessions);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des stocks' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadDailyOverview = async () => {
    setDailyLoading(true);
    try {
      const response = await api.get('/admin/stocks/daily-overview', { params: { brand: BRAND } });
      setDailyData(response.data);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement de la vue journalière' });
    } finally {
      setDailyLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') { navigate('/dashboard'); return; }
    loadStocks();
    api.get('/admin/cities', { params: { brand: BRAND } }).then(r => setCities(r.data)).catch(() => {});
    api.get('/admin/pdv', { params: { brand: BRAND } }).then(r => setPdvs(r.data)).catch(() => {});
    loadDailyOverview();
  }, [user, navigate, loadStocks]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        body { background: white !important; color: black !important; }
        nav, header, .navbar { display: none !important; }
        .modal { display: none !important; }
        .min-vh-100 { min-height: auto !important; background: white !important; }
        h1, h2, h3, h4, h5, h6, p, span, div, small, td, th { color: #000 !important; }
        button { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().split('T')[0];
    if (stocksData && stocksData.length > 0) {
      const sheet = [];
      for (const session of stocksData) {
        for (const stock of session.stocks || []) {
          sheet.push({
            'PDV': session.pdv_name || '', 'Ville': session.city_name || '',
            'Lot': stock.prize_label, 'Type': stock.prize_type,
            'Stock initial': stock.max_quantity || 0, 'Utilisé': stock.used_quantity || 0,
            'Restant': stock.remaining_quantity || 0, 'Statut': stock.status || ''
          });
        }
      }
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheet), 'Stocks globaux');
    }
    if (dailyData && dailyData.pdvs && dailyData.days) {
      const dayCols = dailyData.days.map((day, i) => `J${i + 1} (${day})`);
      const sheet = [];
      for (const pdv of dailyData.pdvs) {
        for (const prize of pdv.prizes || []) {
          const row = { 'Ville': pdv.city_name || '', 'PDV': pdv.pdv_name || '', 'Lot': prize.prize_label, 'Initial': prize.initial_quantity || 0 };
          (prize.days || []).forEach((cell, i) => { row[dayCols[i] || `J${i + 1}`] = cell.wins ?? 0; });
          row['Restant'] = prize.remaining_quantity ?? 0;
          sheet.push(row);
        }
      }
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheet), 'Vue journalière');
    }
    XLSX.writeFile(workbook, `stocks_nature_power_${today}.xlsx`);
  };

  const handleRestock = async () => {
    if (!restockPrize || restockQty <= 0) return;
    try {
      await api.post('/admin/stocks/restock', {
        prize_config_id: restockPrize.prize_id, session_id: restockPrize.session_id,
        quantity_added: restockQty, restock_date: restockDate
      });
      setMessage({ type: 'success', text: `+${restockQty} ${restockPrize.prize_label} ajoutés` });
      setShowRestockModal(false);
      setRestockQty(0);
      loadDailyOverview();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur réapprovisionnement' });
    }
  };

  const handleSaveGainMode = async () => {
    try {
      await api.put(`/admin/sessions/${selectedSessionForGainMode.session_id}`, { gain_mode: newGainMode, brand: BRAND });
      setMessage({ type: 'success', text: 'Mode de gain mis à jour' });
      setShowGainModeModal(false);
      loadStocks();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur mise à jour' });
    }
  };

  const filteredDailyPdvs = useMemo(() => {
    if (!dailyData?.pdvs) return [];
    let list = dailyData.pdvs;
    if (filters.city_id) {
      const city = cities.find(c => String(c.id) === String(filters.city_id));
      if (city) list = list.filter(p => p.city_name === city.name);
    }
    if (filters.pdv_id) list = list.filter(p => String(p.pdv_id) === String(filters.pdv_id));
    return list;
  }, [dailyData, filters, cities]);

  const totalsByCity = useMemo(() => {
    if (!stocksData?.length) return [];
    const byCity = new Map();
    for (const session of stocksData) {
      const city = session.city_name || 'N/A';
      const cur = byCity.get(city) || { city_name: city, sessions_count: 0, max_quantity: 0, used_quantity: 0, remaining_quantity: 0 };
      cur.sessions_count++;
      for (const stock of session.stocks || []) {
        cur.max_quantity += stock.max_quantity || 0;
        cur.used_quantity += stock.used_quantity || 0;
        cur.remaining_quantity += stock.remaining_quantity || 0;
      }
      byCity.set(city, cur);
    }
    return Array.from(byCity.values()).sort((a, b) => a.city_name.localeCompare(b.city_name));
  }, [stocksData]);

  const getStockBadge = (status) => {
    const cfg = {
      empty: { label: 'Épuisé', color: '#e53935', bg: 'rgba(229,57,53,0.15)', border: 'rgba(229,57,53,0.4)' },
      low: { label: 'Critique', color: '#f57c00', bg: 'rgba(245,124,0,0.15)', border: 'rgba(245,124,0,0.4)' },
      medium: { label: 'Moyen', color: '#1890ff', bg: 'rgba(24,144,255,0.15)', border: 'rgba(24,144,255,0.4)' },
    }[status] || { label: 'OK', color: '#00A651', bg: 'rgba(0,192,96,0.15)', border: 'rgba(0,192,96,0.4)' };
    return <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>{cfg.label}</span>;
  };

  const getProgress = (pct, status) => {
    const color = status === 'empty' ? '#e53935' : status === 'low' ? '#f57c00' : status === 'medium' ? '#1890ff' : G;
    return (
      <div style={{ height: '6px', background: 'rgba(0,0,0,0.1)', borderRadius: '3px', overflow: 'hidden', minWidth: '100px' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px' }}></div>
      </div>
    );
  };

  const tabBtn = (id, label, icon) => (
    <button onClick={() => setActiveTab(id)} style={{
      background: 'none', border: 'none', padding: '10px 20px', cursor: 'pointer', fontWeight: 700,
      color: activeTab === id ? G : 'rgba(0,0,0,0.45)',
      borderBottom: activeTab === id ? `3px solid ${G}` : '3px solid transparent', fontSize: '14px'
    }}>
      <i className={`fas ${icon} me-2`}></i>{label}
    </button>
  );

  if (loading) return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: BG }}>
      <div className="spinner-border" style={{ color: G }} role="status"></div>
    </div>
  );

  return (
    <div className="min-vh-100" style={{ background: BG }}>
      <nav className="navbar navbar-dark sticky-top" style={{ background: '#0a1f0c', padding: '10px 0', borderBottom: `2px solid rgba(0,192,96,0.4)` }}>
        <div className="container d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <img src="/logo-white.png" alt="RNP" style={{ width: '46px', height: 'auto' }} />
            <div>
              <div className="fw-bold text-white" style={{ fontSize: '16px' }}>Reaktor Nature Power</div>
              <div style={{ fontSize: '11px', color: G }}>Gestion des Stocks</div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <span className="text-white d-none d-md-block" style={{ fontSize: '13px' }}>
              <i className="fas fa-user-shield me-1" style={{ color: G }}></i>{user?.username}
            </span>
            <button onClick={() => navigate('/dashboard')} className="btn btn-sm"
              style={{ background: 'rgba(0,192,96,0.2)', border: `1px solid rgba(0,192,96,0.5)`, color: '#fff', fontSize: '13px' }}>
              <i className="fas fa-arrow-left me-2"></i>Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close btn-close-white" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="fw-bold mb-1" style={{ color: '#1a3322', fontSize: '26px' }}>
              <i className="fas fa-warehouse me-2" style={{ color: G }}></i>Stocks
            </h2>
            <p style={{ color: 'rgba(0,0,0,0.5)', margin: 0 }}>{stocksData?.length || 0} session(s) active(s)</p>
          </div>
          <div className="d-flex gap-2">
            <button onClick={handleExportExcel} className="btn btn-sm"
              style={{ background: '#1D7A3A', color: '#fff', border: 'none', fontWeight: 700, fontSize: '13px' }}
              disabled={!stocksData?.length && !dailyData?.pdvs?.length}>
              <i className="fas fa-file-excel me-2"></i>Exporter Excel
            </button>
            <button onClick={() => window.print()} className="btn btn-sm"
              style={{ background: G, color: '#fff', border: 'none', fontWeight: 700, fontSize: '13px' }}>
              <i className="fas fa-print me-2"></i>Imprimer
            </button>
          </div>
        </div>

        <div style={{ background: CARD, borderRadius: '12px 12px 0 0', border: `1px solid ${BORDER}`, borderBottom: 'none', marginBottom: '0' }}>
          <div style={{ borderBottom: `2px solid rgba(0,192,96,0.3)`, paddingLeft: '8px' }}>
            {tabBtn('daily', 'Vue journalière', 'fa-calendar-alt')}
            {tabBtn('stocks', 'Stocks globaux', 'fa-boxes')}
          </div>
        </div>

        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderTop: 'none', padding: '16px 20px', marginBottom: '20px', borderRadius: '0 0 12px 12px', boxShadow: SHADOW }}>
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Ville</label>
              <select className="form-select form-select-sm" value={filters.city_id}
                onChange={e => setFilters({ ...filters, city_id: e.target.value, pdv_id: '' })}
                style={{ ...inputStyle, fontSize: '13px' }}>
                <option value="">Toutes les villes</option>
                {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Point de Vente</label>
              <select className="form-select form-select-sm" value={filters.pdv_id}
                onChange={e => setFilters({ ...filters, pdv_id: e.target.value })}
                disabled={!filters.city_id} style={{ ...inputStyle, fontSize: '13px' }}>
                <option value="">Tous les PDV</option>
                {pdvs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {(filters.city_id || filters.pdv_id) && (
              <div className="col-md-2">
                <button onClick={() => setFilters({ city_id: '', pdv_id: '' })} className="btn btn-sm w-100"
                  style={{ background: 'rgba(0,0,0,0.06)', color: '#1a3322', border: '1px solid rgba(0,0,0,0.15)', fontSize: '12px' }}>
                  <i className="fas fa-times me-1"></i>Réinitialiser
                </button>
              </div>
            )}
          </div>
        </div>

        {activeTab === 'daily' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={{ color: '#1a3322', margin: 0, fontWeight: 700 }}>Stocks par Point de Vente</h5>
              <button onClick={loadDailyOverview} className="btn btn-sm"
                style={{ background: 'rgba(0,192,96,0.15)', border: `1px solid rgba(0,192,96,0.4)`, color: G, fontSize: '12px', fontWeight: 600 }}>
                <i className="fas fa-sync-alt me-1"></i>Actualiser
              </button>
            </div>

            {dailyLoading ? (
              <div className="text-center py-5"><div className="spinner-border" style={{ color: G }} role="status"></div></div>
            ) : filteredDailyPdvs.length === 0 ? (
              <div style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, padding: '50px', textAlign: 'center' }}>
                <i className="fas fa-box-open fa-3x d-block mb-3" style={{ color: G, opacity: 0.3 }}></i>
                <p style={{ color: 'rgba(0,0,0,0.35)' }}>Aucune donnée disponible</p>
              </div>
            ) : filteredDailyPdvs.map(pdv => {
              const hasAlert = pdv.prizes?.some(p => p.remaining_quantity <= 5 && p.remaining_quantity > 0);
              const isEmpty = pdv.prizes?.some(p => p.remaining_quantity === 0 && p.initial_quantity > 0);
              return (
                <div key={pdv.pdv_id} style={{ background: CARD, borderRadius: '16px', marginBottom: '20px', border: `1px solid ${isEmpty ? 'rgba(229,57,53,0.5)' : hasAlert ? 'rgba(245,124,0,0.4)' : BORDER}`, boxShadow: SHADOW, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', background: 'rgba(0,166,81,0.08)', borderBottom: `1px solid rgba(0,192,96,0.2)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ color: '#1a3322', fontWeight: 700, fontSize: '15px' }}>
                        <i className="fas fa-store me-2" style={{ color: G }}></i>{pdv.pdv_name}
                      </span>
                      {pdv.city_name && <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: '12px', marginLeft: '8px' }}>{pdv.city_name}</span>}
                    </div>
                    <div className="d-flex gap-2">
                      {isEmpty && <span style={{ padding: '3px 10px', borderRadius: '12px', background: 'rgba(229,57,53,0.15)', color: '#e53935', fontSize: '11px', fontWeight: 700, border: '1px solid rgba(229,57,53,0.4)' }}>Épuisé</span>}
                      {hasAlert && !isEmpty && <span style={{ padding: '3px 10px', borderRadius: '12px', background: 'rgba(245,124,0,0.15)', color: '#f57c00', fontSize: '11px', fontWeight: 700, border: '1px solid rgba(245,124,0,0.4)' }}>Stock faible</span>}
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'linear-gradient(135deg, #00C060 0%, #00A651 100%)' }}>
                          <th style={{ padding: '10px 16px', color: '#1a3322', fontSize: '11px', fontWeight: 700, textAlign: 'left', position: 'sticky', left: 0, background: '#00A651', minWidth: '130px' }}>Lot</th>
                          <th style={{ padding: '10px 12px', color: '#fff', fontSize: '11px', fontWeight: 700, textAlign: 'center', minWidth: '60px' }}>Initial</th>
                          {dailyData?.days?.map((day, i) => (
                            <th key={day} style={{ padding: '10px 8px', color: '#fff', fontSize: '11px', fontWeight: 700, textAlign: 'center', minWidth: '80px' }}>
                              J{i+1} · {new Date(day + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                            </th>
                          ))}
                          <th style={{ padding: '10px 12px', color: '#fff', fontSize: '11px', fontWeight: 700, textAlign: 'center', minWidth: '70px' }}>Restant</th>
                          <th style={{ padding: '10px 12px', color: '#fff', fontSize: '11px', fontWeight: 700, textAlign: 'center', minWidth: '80px' }}>Réappro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pdv.prizes?.map((prize, idx) => {
                          const rem = prize.remaining_quantity;
                          const remAlert = rem <= 5 && rem > 0;
                          const remEmpty = rem === 0;
                          return (
                            <tr key={prize.prize_type} style={{ borderBottom: `1px solid rgba(0,192,96,0.15)`, background: idx % 2 === 0 ? 'rgba(0,166,81,0.04)' : '#ffffff' }}>
                              <td style={{ padding: '12px 16px', position: 'sticky', left: 0, background: idx % 2 === 0 ? 'rgba(0,166,81,0.04)' : '#ffffff' }}>
                                <div className="d-flex align-items-center gap-2">
                                  {prize.color && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: prize.color, display: 'inline-block' }}></span>}
                                  <span style={{ color: '#1a3322', fontSize: '13px', fontWeight: 600 }}>{prize.prize_label}</span>
                                </div>
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center', color: 'rgba(0,0,0,0.55)', fontSize: '13px' }}>{prize.initial_quantity}</td>
                              {prize.days?.map(cell => {
                                const a = cell.stock_start <= 5 && cell.stock_start > 0;
                                const e = cell.stock_start === 0;
                                return (
                                  <td key={cell.date} style={{ padding: '10px 8px', textAlign: 'center', background: e ? 'rgba(229,57,53,0.1)' : a ? 'rgba(245,124,0,0.08)' : 'transparent' }}>
                                    <span style={{ color: e ? '#e53935' : a ? '#f57c00' : '#1a3322', fontWeight: 'bold', fontSize: '13px' }}>
                                      {cell.stock_start}
                                    </span>
                                    {cell.wins > 0 && <div style={{ fontSize: '9px', color: '#e53935' }}>−{cell.wins}</div>}
                                    {cell.restock > 0 && <div style={{ fontSize: '9px', color: '#00A651' }}>+{cell.restock}</div>}
                                  </td>
                                );
                              })}
                              <td style={{ padding: '12px', textAlign: 'center', background: remEmpty ? 'rgba(229,57,53,0.1)' : 'transparent' }}>
                                <span style={{ fontWeight: 800, fontSize: '15px', color: remEmpty ? '#e53935' : remAlert ? '#f57c00' : '#00A651' }}>
                                  {rem}
                                </span>
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                <button style={{ background: `linear-gradient(135deg, ${G}, ${G_DARK})`, color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                                  onClick={() => { setRestockPrize({ ...prize, pdv_name: pdv.pdv_name }); setRestockQty(0); setRestockDate(new Date().toISOString().split('T')[0]); setShowRestockModal(true); }}>
                                  <i className="fas fa-plus me-1"></i>Réappro
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {(() => {
                          const tryMap = pdv.try_again_by_day || {};
                          const total = Object.values(tryMap).reduce((a, b) => a + b, 0);
                          return (
                            <tr style={{ borderTop: `1px dashed rgba(0,192,96,0.3)`, background: 'rgba(0,166,81,0.06)' }}>
                              <td style={{ padding: '10px 16px', position: 'sticky', left: 0, background: 'rgba(0,166,81,0.06)' }}>
                                <span style={{ color: 'rgba(0,0,0,0.35)', fontSize: '13px' }}>Réessayez</span>
                              </td>
                              <td style={{ textAlign: 'center', color: 'rgba(0,0,0,0.3)', fontSize: '13px' }}>—</td>
                              {dailyData?.days?.map(day => (
                                <td key={day} style={{ textAlign: 'center' }}>
                                  <span style={{ color: (tryMap[day] || 0) > 0 ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.25)', fontSize: '13px' }}>
                                    {tryMap[day] > 0 ? tryMap[day] : '—'}
                                  </span>
                                </td>
                              ))}
                              <td style={{ textAlign: 'center', color: total > 0 ? '#1a3322' : 'rgba(0,0,0,0.25)', fontWeight: 700 }}>{total > 0 ? total : '—'}</td>
                              <td></td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'stocks' && (
          <>
            {totalsByCity.length > 0 && (
              <div style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, marginBottom: '20px', overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', background: 'rgba(0,166,81,0.08)', borderBottom: `1px solid rgba(0,192,96,0.2)` }}>
                  <h5 style={{ color: '#1a3322', margin: 0, fontWeight: 700 }}><i className="fas fa-city me-2" style={{ color: G }}></i>Totaux par ville</h5>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg, #00C060 0%, #00A651 100%)' }}>
                        {['Ville', 'Sessions', 'Stock Initial', 'Utilisé', 'Restant', 'Progression'].map(h => (
                          <th key={h} style={{ padding: '12px 16px', color: '#1a3322', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', textAlign: h === 'Ville' ? 'left' : 'center', letterSpacing: '0.5px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {totalsByCity.map((row, idx) => {
                        const pct = row.max_quantity > 0 ? (row.remaining_quantity / row.max_quantity) * 100 : 0;
                        const status = row.max_quantity > 0 && pct === 0 ? 'empty' : pct < 20 ? 'low' : pct < 50 ? 'medium' : 'ok';
                        return (
                          <tr key={row.city_name} style={{ borderBottom: `1px solid rgba(0,192,96,0.15)`, background: idx % 2 === 0 ? 'rgba(0,166,81,0.04)' : '#ffffff' }}>
                            <td style={{ padding: '12px 16px', color: '#1a3322', fontWeight: 600 }}>{row.city_name}</td>
                            <td style={{ padding: '12px', textAlign: 'center', color: '#2a5a3a' }}>{row.sessions_count}</td>
                            <td style={{ padding: '12px', textAlign: 'center', color: '#2a5a3a' }}>{row.max_quantity}</td>
                            <td style={{ padding: '12px', textAlign: 'center', color: '#2a5a3a' }}>{row.used_quantity}</td>
                            <td style={{ padding: '12px', textAlign: 'center', fontWeight: 800, fontSize: '16px', color: status === 'empty' ? '#e53935' : status === 'low' ? '#f57c00' : '#00A651' }}>{row.remaining_quantity}</td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>{getProgress(Math.round(pct), status)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!stocksData?.length ? (
              <div style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, padding: '60px', textAlign: 'center' }}>
                <i className="fas fa-box-open fa-4x d-block mb-3" style={{ color: G, opacity: 0.3 }}></i>
                <p style={{ color: 'rgba(0,0,0,0.35)' }}>Aucune session active trouvée</p>
              </div>
            ) : stocksData.map(session => (
              <div key={session.session_id} style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, marginBottom: '20px', overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', background: 'rgba(0,166,81,0.08)', borderBottom: `1px solid rgba(0,192,96,0.2)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#00A651', fontWeight: 700, fontSize: '15px' }}><i className="fas fa-store me-2"></i>{session.pdv_name}</div>
                    <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: '12px', marginTop: '2px' }}>
                      {session.city_name} · {session.session_name} ·
                      <span style={{ marginLeft: '6px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700,
                        background: session.gain_mode === 'FORCED' ? 'rgba(229,57,53,0.15)' : session.gain_mode === 'RANDOM' ? 'rgba(24,144,255,0.15)' : 'rgba(0,192,96,0.15)',
                        color: session.gain_mode === 'FORCED' ? '#e53935' : session.gain_mode === 'RANDOM' ? '#1890ff' : '#00A651',
                        border: `1px solid ${session.gain_mode === 'FORCED' ? 'rgba(229,57,53,0.4)' : session.gain_mode === 'RANDOM' ? 'rgba(24,144,255,0.4)' : 'rgba(0,192,96,0.4)'}` }}>
                        {session.gain_mode === 'FORCED' ? 'Gagnant' : session.gain_mode === 'RANDOM' ? 'Aléatoire' : 'Probabilité'}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => { setSelectedSessionForGainMode(session); setNewGainMode(session.gain_mode || 'PROBABILITY'); setShowGainModeModal(true); }}
                    style={{ padding: '6px 12px', background: 'rgba(0,192,96,0.15)', color: '#00A651', border: `1px solid rgba(0,192,96,0.4)`, borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                    <i className="fas fa-cog me-1"></i>Mode de gain
                  </button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg, #00C060 0%, #00A651 100%)' }}>
                        {['Lot', 'Stock Initial', 'Stock ajouté', 'Utilisé', 'Restant', 'Statut', 'Progression'].map(h => (
                          <th key={h} style={{ padding: '12px 16px', color: '#1a3322', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', textAlign: h === 'Lot' ? 'left' : 'center', letterSpacing: '0.5px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {session.stocks?.map((stock, idx) => (
                        <tr key={stock.prize_type} style={{ borderBottom: `1px solid rgba(0,192,96,0.15)`, background: idx % 2 === 0 ? 'rgba(0,166,81,0.04)' : '#ffffff' }}>
                          <td style={{ padding: '12px 16px' }}>
                            <div className="d-flex align-items-center gap-2">
                              {stock.color && <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: stock.color, flexShrink: 0 }}></div>}
                              <div>
                                <div style={{ color: '#1a3322', fontWeight: 600, fontSize: '14px' }}>{stock.prize_label}</div>
                                {stock.prize_name && <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: '12px' }}>{stock.prize_name}</div>}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#2a5a3a' }}>{stock.max_quantity}</td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#00A651', fontWeight: 700 }}>
                            {addedStockMap[`${session.session_id}-${stock.prize_type}`] || 0}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#2a5a3a' }}>{stock.used_quantity}</td>
                          <td style={{ padding: '12px', textAlign: 'center', fontWeight: 800, fontSize: '16px', color: stock.status === 'empty' ? '#e53935' : stock.status === 'low' ? '#f57c00' : '#00A651' }}>{stock.remaining_quantity}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{getStockBadge(stock.status)}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{getProgress(stock.percentage, stock.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {showRestockModal && restockPrize && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#ffffff', border: `1px solid rgba(0,192,96,0.4)`, borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
              <div className="modal-header" style={{ borderBottom: `1px solid rgba(0,192,96,0.3)`, background: 'rgba(0,0,0,0.3)', borderRadius: '16px 16px 0 0' }}>
                <h5 className="modal-title fw-bold" style={{ color: '#1a3322' }}>
                  <i className="fas fa-plus-circle me-2" style={{ color: G }}></i>Réapprovisionnement
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowRestockModal(false)}></button>
              </div>
              <div className="modal-body">
                <div style={{ padding: '12px 16px', background: 'rgba(0,0,0,0.25)', borderRadius: '10px', border: `1px solid rgba(0,192,96,0.3)`, marginBottom: '16px' }}>
                  <div style={{ color: 'rgba(0,0,0,0.5)', fontSize: '13px', marginBottom: '4px' }}>PDV : <strong style={{ color: '#1a3322' }}>{restockPrize.pdv_name}</strong></div>
                  <div style={{ color: 'rgba(0,0,0,0.5)', fontSize: '13px' }}>Lot : <strong style={{ color: '#1a3322' }}>{restockPrize.prize_label}</strong></div>
                </div>
                <div className="mb-3">
                  <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Date</label>
                  <input type="date" className="form-control" value={restockDate} onChange={e => setRestockDate(e.target.value)} style={inputStyle} />
                </div>
                <div className="mb-3">
                  <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Quantité à ajouter</label>
                  <div className="input-group">
                    <button className="btn" onClick={() => setRestockQty(q => Math.max(0, q - 1))}
                      style={{ background: 'rgba(0,192,96,0.15)', borderColor: 'rgba(0,192,96,0.4)', color: '#00A651' }}>
                      <i className="fas fa-minus"></i>
                    </button>
                    <input type="number" className="form-control text-center fw-bold" value={restockQty}
                      onChange={e => setRestockQty(Math.max(0, parseInt(e.target.value) || 0))}
                      style={{ ...inputStyle, fontSize: '20px' }} />
                    <button className="btn" onClick={() => setRestockQty(q => q + 1)}
                      style={{ background: 'rgba(0,192,96,0.15)', borderColor: 'rgba(0,192,96,0.4)', color: '#00A651' }}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(0,0,0,0.25)', borderRadius: '10px', border: `1px solid rgba(0,192,96,0.3)` }}>
                  <span style={{ color: 'rgba(0,0,0,0.5)', fontSize: '13px' }}>
                    Stock actuel : <strong style={{ color: '#1a3322' }}>{restockPrize.remaining_quantity}</strong>
                    {' → '}Après : <strong style={{ color: '#00A651' }}>{restockPrize.remaining_quantity + restockQty}</strong>
                  </span>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: `1px solid rgba(0,192,96,0.3)` }}>
                <button className="btn fw-bold" onClick={() => setShowRestockModal(false)}
                  style={{ background: 'rgba(0,0,0,0.06)', color: '#1a3322', border: '1px solid rgba(0,0,0,0.15)' }}>Annuler</button>
                <button className="btn fw-bold" disabled={restockQty <= 0} onClick={handleRestock}
                  style={{ background: `linear-gradient(135deg, ${G}, ${G_DARK})`, color: '#fff', border: 'none', padding: '8px 24px', boxShadow: '0 4px 14px rgba(0,192,96,0.3)' }}>
                  <i className="fas fa-check me-2"></i>Confirmer +{restockQty}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showGainModeModal && selectedSessionForGainMode && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#ffffff', border: `1px solid rgba(0,192,96,0.4)`, borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
              <div className="modal-header" style={{ borderBottom: `1px solid rgba(0,192,96,0.3)`, background: 'rgba(0,0,0,0.3)', borderRadius: '16px 16px 0 0' }}>
                <h5 className="modal-title fw-bold" style={{ color: '#1a3322' }}>
                  <i className="fas fa-cog me-2" style={{ color: G }}></i>Mode de gain
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowGainModeModal(false)}></button>
              </div>
              <div className="modal-body">
                <div style={{ padding: '10px 14px', background: 'rgba(0,0,0,0.25)', borderRadius: '10px', border: `1px solid rgba(0,192,96,0.3)`, marginBottom: '16px' }}>
                  <span style={{ color: 'rgba(0,0,0,0.5)', fontSize: '13px' }}>PDV : <strong style={{ color: '#1a3322' }}>{selectedSessionForGainMode.pdv_name}</strong></span>
                </div>
                <div className="mb-3">
                  <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '8px' }}>Nouveau mode</label>
                  <select className="form-select" value={newGainMode} onChange={e => setNewGainMode(e.target.value)} style={inputStyle}>
                    <option value="PROBABILITY">Probabilité (ratio configuré)</option>
                    <option value="FORCED">Gagnant (100% de gain)</option>
                    <option value="RANDOM">Aléatoire (tirage au sort)</option>
                  </select>
                  <div style={{ marginTop: '10px', padding: '10px', background: 'rgba(0,0,0,0.25)', borderRadius: '8px', border: `1px solid rgba(0,192,96,0.3)` }}>
                    <small style={{ color: 'rgba(0,0,0,0.5)' }}>
                      {newGainMode === 'PROBABILITY' && <><i className="fas fa-info-circle me-1" style={{ color: G }}></i>Chaque lot a sa probabilité selon le ratio configuré</>}
                      {newGainMode === 'FORCED' && <><i className="fas fa-exclamation-triangle me-1" style={{ color: '#f57c00' }}></i><strong style={{ color: '#f57c00' }}>Tous les joueurs gagnent !</strong></>}
                      {newGainMode === 'RANDOM' && <><i className="fas fa-random me-1" style={{ color: '#1890ff' }}></i>Tirage aléatoire parmi tous les lots</>}
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: `1px solid rgba(0,192,96,0.3)` }}>
                <button className="btn fw-bold" onClick={() => setShowGainModeModal(false)}
                  style={{ background: 'rgba(0,0,0,0.06)', color: '#1a3322', border: '1px solid rgba(0,0,0,0.15)' }}>Annuler</button>
                <button className="btn fw-bold" onClick={handleSaveGainMode}
                  style={{ background: `linear-gradient(135deg, ${G}, ${G_DARK})`, color: '#fff', border: 'none', padding: '8px 24px', boxShadow: '0 4px 14px rgba(0,192,96,0.3)' }}>
                  <i className="fas fa-save me-2"></i>Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stocks;
